import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const initialState = {
  name: "",
  email: "",
  phone: "",
  event_date: "",
  event_type: "",
  location: "",
  message: "",
};

const eventTypes = [
  "Svadba",
  "Oslava / Výročie",
  "Firemná akcia",
  "Fotografovanie",
  "Iné",
];

export default function Contact() {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Ďakujeme! Váš dopyt sme prijali. Ozveme sa do 24 hodín.");
      setForm(initialState);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Nepodarilo sa odoslať dopyt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative py-24 sm:py-36 bg-[#0a0a0a] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left details */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <span className="ornament mb-6">Kontakt</span>
          <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-cream mt-6 leading-[1.05]">
            Rezervujte si
            <br />
            <em className="text-gold italic font-light">termín.</em>
          </h2>
          <p className="mt-8 text-cream/70 text-base leading-relaxed font-light max-w-sm">
            Cena prenájmu je individuálna a stanovuje sa dohodou podľa termínu,
            trasy a rozsahu služieb. Ozvite sa nám — radi pripravíme ponuku len
            pre vás.
          </p>

          <div className="mt-12 space-y-6">
            <div className="flex items-start gap-4">
              <Phone size={18} className="text-gold mt-1" strokeWidth={1.3} />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold/80">
                  Telefón
                </div>
                <a
                  href="tel:+421907380990"
                  data-testid="contact-phone-1"
                  className="text-cream link-underline mt-1 inline-block"
                >
                  +421 907 380 990
                </a>
                <span className="text-cream/40 text-xs ml-2">Marek</span>
                <br />
                <a
                  href="tel:+421915230681"
                  data-testid="contact-phone-2"
                  className="text-cream link-underline mt-1 inline-block"
                >
                  +421 915 230 681
                </a>
                <span className="text-cream/40 text-xs ml-2">Dávid</span>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Mail size={18} className="text-gold mt-1" strokeWidth={1.3} />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold/80">
                  E-mail
                </div>
                <a
                  href="mailto:tatra603david@gmail.com"
                  data-testid="contact-email"
                  className="text-cream link-underline mt-1 inline-block"
                >
                  tatra603david@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin size={18} className="text-gold mt-1" strokeWidth={1.3} />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold/80">
                  Pôsobnosť
                </div>
                <div className="text-cream mt-1">Prievidza · celé Slovensko</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right form */}
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15 }}
          onSubmit={handleSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 bg-[#111111] border border-white/10 p-8 sm:p-12 grain"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
            <div className="sm:col-span-1">
              <label className="lux-label" htmlFor="name">Meno a priezvisko</label>
              <input
                id="name"
                name="name"
                required
                value={form.name}
                onChange={handleChange}
                data-testid="form-name"
                placeholder="Jana Novákova"
                className="lux-input"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="lux-label" htmlFor="email">E-mail</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={form.email}
                onChange={handleChange}
                data-testid="form-email"
                placeholder="jana@email.sk"
                className="lux-input"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="lux-label" htmlFor="phone">Telefón</label>
              <input
                id="phone"
                name="phone"
                required
                value={form.phone}
                onChange={handleChange}
                data-testid="form-phone"
                placeholder="+421 ..."
                className="lux-input"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="lux-label" htmlFor="event_date">Dátum udalosti</label>
              <input
                id="event_date"
                name="event_date"
                type="date"
                value={form.event_date}
                onChange={handleChange}
                data-testid="form-event-date"
                className="lux-input"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="lux-label" htmlFor="event_type">Typ udalosti</label>
              <select
                id="event_type"
                name="event_type"
                value={form.event_type}
                onChange={handleChange}
                data-testid="form-event-type"
                className="lux-input"
              >
                <option value="" className="bg-[#111]">Vyberte…</option>
                {eventTypes.map((t) => (
                  <option key={t} value={t} className="bg-[#111]">
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-1">
              <label className="lux-label" htmlFor="location">Miesto / Trasa</label>
              <input
                id="location"
                name="location"
                value={form.location}
                onChange={handleChange}
                data-testid="form-location"
                placeholder="Bratislava — Modra"
                className="lux-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="lux-label" htmlFor="message">Vaša správa</label>
              <textarea
                id="message"
                name="message"
                required
                rows={4}
                value={form.message}
                onChange={handleChange}
                data-testid="form-message"
                placeholder="Napíšte nám o vašom dni — predstavách, trasách, hodinách…"
                className="lux-input resize-none"
              />
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <p className="text-xs text-cream/50 max-w-xs leading-relaxed">
              Odoslaním súhlasíte so spracovaním kontaktných údajov za účelom
              odpovede na váš dopyt.
            </p>
            <button
              type="submit"
              disabled={loading}
              data-testid="form-submit"
              className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Odosielam…" : "Odoslať dopyt"}
              <Send size={14} strokeWidth={1.5} />
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
